import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  TextField,
  Backdrop,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { doc, getDoc, updateDoc, collection, getDocs, deleteDoc, query, where, collectionGroup, setDoc } from 'firebase/firestore';
import { deleteUser, sendPasswordResetEmail } from 'firebase/auth';
import { AppContext } from '../AppContext';
import { db, storage, auth } from '../firebaseConfig';
import { trackFeature } from '../utils/analytics';
import { listAll, deleteObject, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ConfirmationDialog from '../components/ConfirmationDialog';
import UserAvatar from '../components/UserAvatar';
import { SnackbarContext } from '../SnackbarContext';
import { resizeImageToSquare } from '../utils/imageProcessing';
// UserInfo component (unchanged)
function UserInfo({ user, userData, showPasswordResetDialog }) {
  // Local states for editing Display Name
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState(userData?.displayName || '');

  const { showSuccess, showError } = useContext(SnackbarContext);

  const getMemberFarmIdsForUser = async (uid) => {
    if (!uid) return [];
    try {
      const membersSnap = await getDocs(collectionGroup(db, 'members'));
      return Array.from(
        new Set(
          membersSnap.docs
            .filter((memberSnap) => memberSnap.id === uid)
            .map((memberSnap) => memberSnap.ref.parent.parent?.id)
            .filter(Boolean)
        )
      );
    } catch {
      return [];
    }
  };

  const syncMemberProfileAcrossFarms = async (uid, patch = {}) => {
    if (!uid) return;
    const farmIds = await getMemberFarmIdsForUser(uid);
    if (farmIds.length === 0) return;

    await Promise.allSettled(
      farmIds.map((farmId) =>
        setDoc(doc(db, 'farms', farmId, 'members', uid), patch, { merge: true })
      )
    );
  };

  if (!userData) {
    return <Typography>No user data available.</Typography>;
  }

  // Save new display name to Firestore
  const handleSaveName = async () => {
    try {
      const trimmedName = tempName.trim();

      // 1) Check if empty
      if (!trimmedName) {
        showError('Display name cannot be empty.');
        return;
      }

      // 2) Check max length (e.g., 20 characters)
      if (trimmedName.length > 20) {
        showError('Display name cannot exceed 20 characters.');
        return;
      }

      await updateDoc(doc(db, 'users', userData.uid), {
        displayName: trimmedName,
      });

      await syncMemberProfileAcrossFarms(userData.uid, { displayName: trimmedName });

      showSuccess('Display name updated successfully.');
      trackFeature('display_name_updated');
      setEditingName(false);
    } catch (err) {
      showError('Failed to update display name.');
    }
  };

  const handleEnterSave = (event, saveFn) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveFn();
    }
  };

  // ---- Avatar Upload Functionality ----
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userData?.uid) return;

    try {
      // 1) Check file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        showError('Please upload a PNG, JPG, or WEBP image.');
        return;
      }

      // 2) Check file size (2 MB limit)
      const maxSizeInBytes = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSizeInBytes) {
        showError('File size cannot exceed 2MB.');
        return;
      }

      const resizedBlob = await resizeImageToSquare(file, 256);
      const avatarRef = ref(storage, `avatars/${userData.uid}/avatar.png`);

      const snapshot = await uploadBytes(avatarRef, resizedBlob, {
        contentType: resizedBlob.type,
      });
      const downloadURL = await getDownloadURL(snapshot.ref);

      await updateDoc(doc(db, 'users', userData.uid), {
        photoURL: downloadURL,
      });
      await syncMemberProfileAcrossFarms(userData.uid, { photoURL: downloadURL });

      showSuccess('Avatar uploaded successfully.');
      trackFeature('avatar_uploaded');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      showError(`Failed to upload avatar: ${error?.message || 'Unknown error'}`);
    }
  };

  // ---- Delete Avatar Functionality ----
  const handleDeleteAvatar = async () => {
    if (!userData?.uid) return;
    try {
      const avatarRef = ref(storage, `avatars/${userData.uid}/avatar.png`);
      await deleteObject(avatarRef);
      await updateDoc(doc(db, 'users', userData.uid), {
        photoURL: null,
      });
      await syncMemberProfileAcrossFarms(userData.uid, { photoURL: '' });
      showSuccess('Avatar removed successfully.');
      trackFeature('avatar_removed');
    } catch (error) {
      showError('Failed to remove avatar.');
    }
  };

  return (
    <Table
      size="small"
      sx={{
        '& .MuiTableCell-root': {
          p: { xs: 1, sm: 1.25 },
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <TableBody>
        {/* Display Name Row */}
        <TableRow>
          <TableCell width="30%">Display Name</TableCell>
          <TableCell>
            {!editingName ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{userData.displayName || 'N/A'}</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setTempName(userData.displayName || '');
                    setEditingName(true);
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            ) : (
              <Grid container spacing={1}>
                <Grid item xs={12} sm>
                  <TextField
                    size="small"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onKeyDown={(e) => handleEnterSave(e, handleSaveName)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm="auto">
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    fullWidth
                    onClick={handleSaveName}
                  >
                    Save
                  </Button>
                </Grid>
                <Grid item xs={6} sm="auto">
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() => setEditingName(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>

            )}
          </TableCell>
        </TableRow>
        {/* Email Row */}
        <TableRow>
          <TableCell width="30%">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Account Email</span>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    This is the email you signed up with, this email will be used for any correspondance.
                  </Typography>
                }>
                <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell width="70%">
            <Typography>{userData.email || 'N/A'}</Typography>
          </TableCell>
        </TableRow>

        {/* Reset Password Row - Only if signed in with email/pass */}
        {auth?.currentUser?.providerData[0]?.providerId === 'password' && (
          <TableRow>
            <TableCell width="30%">Reset Password</TableCell>
            <TableCell width="70%">
              <Button
                variant="outlined"
                color="warning"
                onClick={showPasswordResetDialog}
              >
                Reset Password
              </Button>
            </TableCell>
          </TableRow>
        )}


        {/* Avatar Row */}
        <TableRow>
          <TableCell width="30%">Profile Picture</TableCell>
          <TableCell width="70%">
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp"
              id="avatar-upload"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <label htmlFor="avatar-upload">
                  <Button variant="contained" color="secondary" component="span">
                    {userData.photoURL ? 'Change Avatar' : 'Upload Avatar'}
                  </Button>
                </label>
                {userData.photoURL && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleDeleteAvatar}
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <UserAvatar user={user} userData={userData} size={96} />
              </Box>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Allowed file types: PNG, JPG, WEBP. Max size: 2MB.
            </Typography>
          </TableCell>
        </TableRow>

      </TableBody>
    </Table>
  );
}

export default function AccountPage() {
  const { userData, user } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;

  let createdAtString = 'N/A';
  if (userData?.createdAt?.toDate) {
    createdAtString = userData.createdAt.toDate().toLocaleString();
  }
  let expiresString = 'N/A';
  if (userData?.accessExpires?.toDate) {
    const expiresDate = userData.accessExpires.toDate();
    expiresString = expiresDate > new Date() ? expiresDate.toLocaleString() : 'No Active Subscription';
  }

  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');

  const { showSuccess, showError } = useContext(SnackbarContext);

  const farmSubcollectionsToDelete = [
    'customers',
    'harvestTemplates',
    'orderNoteTemplates',
    'seeds',
    'solutions',
    'trays',
    'harvests',
    'tasks',
    'orders',
    'invoices',
    'counters',
    'media',
    'members',
  ];
  const userSubcollectionsToDelete = ['counters'];

  const getMemberFarmIds = async (uid) => {
    if (!uid) return [];
    try {
      const membersSnap = await getDocs(collectionGroup(db, 'members'));
      return membersSnap.docs
        .filter((memberSnap) => memberSnap.id === uid)
        .map((memberSnap) => memberSnap.ref.parent.parent?.id)
        .filter(Boolean);
    } catch (err) {
      console.warn('Failed to list member farms:', err);
      return [];
    }
  };

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmRequiredWord, setConfirmRequiredWord] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSendPasswordReset = async () => {
    try {
      if (!user?.email) {
        showError('No account email found.');
        return;
      }
      await sendPasswordResetEmail(auth, user.email);
      showSuccess(`Password reset email sent to ${user.email}`);
    } catch (err) {
      showError('Failed to send password reset email.');
    } finally {
      setResetPasswordDialogOpen(false);
    }
  };


  // Open Confirmation Dialog with title, message, action, and (optional) required word
  const openConfirmDialog = (title, message, action, requiredWord = null) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmRequiredWord(requiredWord);
    setConfirmOpen(true);
  };

  // Execute confirmed action
  const handleConfirm = async () => {
    setConfirmOpen(false); // close dialog first
    if (confirmAction) {
      await confirmAction();
    }
  };


  // ---- Redeem Code Logic ----
  const handleRedeem = async () => {
    setRedeemMessage('');
    const code = redeemCode.trim();
    if (!code) {
      showError('Please enter a code.');
      return;
    }
    try {
      const codeRef = doc(db, 'accessCodes', code);
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        showError(`Code "${code}" does not exist.`);
        return;
      }
      const codeData = codeSnap.data();
      if (codeData.redeemed) {
        showError(`Code "${code}" is already redeemed.`);
        return;
      }
      // Mark the code as redeemed
      await updateDoc(codeRef, {
        redeemed: true,
        redeemedAt: new Date(),
      });
      // Extend user's subscription if accessDuration exists
      if (userData?.uid && codeData.accessDuration) {
        const userRef = doc(db, 'users', userData.uid);
        const oldExpires = userData.accessExpires?.toDate ? userData.accessExpires.toDate() : new Date();
        const now = new Date();
        const baseDate = now > oldExpires ? now : oldExpires;
        baseDate.setDate(baseDate.getDate() + codeData.accessDuration);
        await updateDoc(userRef, {
          accessExpires: baseDate,
          approved: true,
        });
      } else if (userData?.uid) {
        const userRef = doc(db, 'users', userData.uid);
        await updateDoc(userRef, { approved: true });
      }
      setRedeemCode('');
      showSuccess(`Success! Code "${code}" has been redeemed.`);
    } catch (err) {
      showError('Failed to redeem code.');
    }
  };

  const deleteCollectionDocuments = async (collectionRef) => {
    const snapshot = await getDocs(collectionRef);
    for (const snap of snapshot.docs) {
      await deleteDoc(snap.ref);
    }
  };

  const deleteStoragePrefixRecursive = async (prefixPath) => {
    try {
      const listResult = await listAll(ref(storage, prefixPath));
      await Promise.all(listResult.items.map((itemRef) => deleteObject(itemRef)));
      for (const childPrefix of listResult.prefixes) {
        await deleteStoragePrefixRecursive(childPrefix.fullPath);
      }
    } catch (err) {
      console.warn(`Failed to delete storage path ${prefixPath}:`, err);
    }
  };

  const isOwnerForFarm = async (farmId, uid) => {
    if (!farmId || !uid) return false;
    const farmRef = doc(db, 'farms', farmId);
    const memberRef = doc(db, 'farms', farmId, 'members', uid);
    const [farmSnap, memberSnap] = await Promise.all([getDoc(farmRef), getDoc(memberRef)]);
    if (!farmSnap.exists()) return false;

    const farmPayload = farmSnap.data() || {};
    const ownerIds = Array.isArray(farmPayload.ownerIds) ? farmPayload.ownerIds : [];
    const memberRole = String(memberSnap.data()?.role || '').toLowerCase();

    return ownerIds.includes(uid) || farmPayload.createdBy === uid || memberRole === 'owner';
  };

  const getOwnedFarmIds = async (uid) => {
    const ownedIds = new Set();

    const candidateFarmIds = new Set([activeFarmId].filter(Boolean));

    for (const farmId of candidateFarmIds) {
      if (await isOwnerForFarm(farmId, uid)) {
        ownedIds.add(farmId);
      }
    }

    try {
      const byOwnerIds = await getDocs(query(collection(db, 'farms'), where('ownerIds', 'array-contains', uid)));
      byOwnerIds.docs.forEach((farmDoc) => ownedIds.add(farmDoc.id));
    } catch (err) {
      console.warn('OwnerIds farm lookup failed:', err);
    }

    try {
      const byCreatedBy = await getDocs(query(collection(db, 'farms'), where('createdBy', '==', uid)));
      byCreatedBy.docs.forEach((farmDoc) => ownedIds.add(farmDoc.id));
    } catch (err) {
      console.warn('CreatedBy farm lookup failed:', err);
    }

    return [...ownedIds];
  };

  const deleteFarmData = async (farmId) => {
    for (const subcollection of farmSubcollectionsToDelete) {
      await deleteCollectionDocuments(collection(db, 'farms', farmId, subcollection));
    }
    await deleteStoragePrefixRecursive(`farms/${farmId}`);
    await deleteDoc(doc(db, 'farms', farmId));
  };

  const removeUserFromNonOwnedFarms = async (uid, ownedFarmIds) => {
    const memberFarmIds = await getMemberFarmIds(uid);
    const farmIds = new Set(
      [
        activeFarmId,
        ...memberFarmIds,
      ].filter(Boolean)
    );

    for (const farmId of farmIds) {
      if (!ownedFarmIds.includes(farmId)) {
        try {
          await deleteDoc(doc(db, 'farms', farmId, 'members', uid));
        } catch (err) {
          console.warn(`Failed removing membership in farm ${farmId}:`, err);
        }
      }
    }
  };

  // ------------------------------------------------
  // DANGER ZONE: Deleting Account
  // ------------------------------------------------

  // 4) Delete the entire account (and all user data)
  const handleDeleteAccount = async () => {
    if (!userData?.uid || !user) {
      showError('User not logged in.');
      return;
    }
    openConfirmDialog(
      'Delete Entire Account',
      'Are you sure you want to permanently delete your entire account and all associated data? This cannot be undone! Type "MICROGREEN" below to confirm.',
      async () => {
        setDeleting(true); // show backdrop

        try {
          const ownedFarmIds = await getOwnedFarmIds(userData.uid);
          if (ownedFarmIds.length === 0) {
            showError('No owned farm was found for this account.');
            return;
          }

          for (const farmId of ownedFarmIds) {
            await deleteFarmData(farmId);
          }

          await removeUserFromNonOwnedFarms(userData.uid, ownedFarmIds);

          for (const subcollection of userSubcollectionsToDelete) {
            await deleteCollectionDocuments(collection(db, 'users', userData.uid, subcollection));
          }

          await deleteStoragePrefixRecursive(`avatars/${userData.uid}`);
          await deleteStoragePrefixRecursive(`invoices/${userData.uid}`);
          await deleteStoragePrefixRecursive(`logos/${userData.uid}`);

          await deleteDoc(doc(db, 'users', userData.uid));

          // Must be last
          await deleteUser(user);

          showSuccess('Account and owned farm data deleted.');
          window.location.href = '/';
        } catch (error) {
          showError('Failed to delete account. You may need to re-log.');
        } finally {
          setDeleting(false);
        }
      },
      'MICROGREEN'
    );
  };


  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Typography variant="h4" gutterBottom>
        Account Page
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box>
          <TableContainer component={Paper}>
            <UserInfo
              user={user}
              userData={userData}
              showPasswordResetDialog={() => setResetPasswordDialogOpen(true)}
            />
          </TableContainer>
        </Box>

        <Box>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', py: 1.5 },
                '& .MuiTableCell-body:first-of-type': { fontWeight: 'bold' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell width="45%">Account Created</TableCell>
                  <TableCell width="55%">{createdAtString}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="45%">Subscribed Until</TableCell>
                  <TableCell width="55%">{expiresString}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper}>
            <Table
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', py: 2.5 },
                '& .MuiTableCell-head, & .MuiTableCell-body:first-of-type': { fontWeight: 'bold' },
              }}
            >
              <TableBody>
                <TableRow
                  sx={{
                    display: { xs: 'block', sm: 'table-row' },
                  }}
                >
                  <TableCell
                    sx={{
                      width: { sm: '80%' },
                      display: { xs: 'block', sm: 'table-cell' },
                      borderBottom: { xs: 'none', sm: '1px solid' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: { sm: 120 },
                          fontWeight: 'bold',
                        }}
                      >
                        Enter Timecode
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value)}
                        placeholder="e.g. ABC123"
                      />
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: { sm: '20%' },
                      display: { xs: 'block', sm: 'table-cell' },
                      borderTop: { xs: 'none', sm: '1px solid' },
                      p: { xs: 1.5, sm: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleRedeem}
                        fullWidth={true}
                        sx={{
                          ml: { xs: 0, sm: 1 },
                          mr: { xs: 0, sm: 1 },
                          px: 2,
                          borderRadius: 1,
                        }}
                      >
                        Redeem
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Paper
            sx={{
              px: 2,
              py: 2,
              mt: 2,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: 'none',
              backgroundColor: 'transparent',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <Typography variant="h5" color="error" sx={{ textAlign: 'center' }}>
                  - Danger Zone - Account Deletion
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Warning: This will delete your farm including all farm data in addition to your
                  account data. This cannot be reversed!
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="error"
                  size="large"
                  onClick={handleDeleteAccount}
                  sx={{ minWidth: 220 }}
                >
                  Delete Account
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>


      <ConfirmationDialog
        open={resetPasswordDialogOpen}
        title="Reset Password"
        message={`A password reset email will be sent to: ${user?.email}. Continue?`}
        onConfirm={handleSendPasswordReset}
        onClose={() => setResetPasswordDialogOpen(false)}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
        requiredWord={confirmRequiredWord}
      />

      <Backdrop
        open={deleting}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Deleting account...
        </Typography>
      </Backdrop>

    </Box>
  );
}









